import { Badge } from '@bambi/component-library/src/components/Badge/Badge';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { DateTime } from 'luxon';

import store from 'src/app/store/store';
import { ServiceTypesColumnFilter } from 'src/common/datagridColumnFilters/ServiceTypesColumnFilter';
import {
  PricingSchemePayerRead,
  PricingSchemeRead,
} from 'src/common/external/bambi-api/bambiApi';
import { formatPriceCell } from 'src/common/formatPriceCell';
import { serviceTypes } from 'src/common/serviceTypes';
import { PayersColumnFilter } from 'src/features/trip/management/filters/PayerColumnFilter';
import SpaceTypeColumnFilter from 'src/features/trip/management/filters/SpaceTypeColumnFilter';

import {
  setSelectedPayers,
  setSelectedServiceTypes,
  setSelectedSpaceTypes,
} from '../pricing.slice';
import { RowActions } from './RowActions';

const columnHelper = createColumnHelper<PricingSchemeRead>();

const nameColumnDef = columnHelper.display({
  enableSorting: true,
  id: 'name',
  header: 'Name',
  cell: (props) => props.row.original.name,
  meta: {
    cellStyle: {
      allowTextWrap: true,
      maxWidth: '20rem',
    },
  },
});

const descriptionColumnDef = columnHelper.display({
  header: 'Description',
  cell: (props) => props.row.original.description,
  meta: {
    cellStyle: {
      allowTextWrap: true,
      maxWidth: '20rem',
    },
  },
});

const payersColumnDef = columnHelper.accessor('payers', {
  header: 'Payers',
  cell: (props) => {
    const { payers } = props.row.original;
    const maxVisiblePayers = 5;

    const formatPayersList = (payers: PricingSchemePayerRead[]) =>
      payers.map((payer) => payer.display_name).join(', ');

    const renderPayers = (payers: PricingSchemePayerRead[]) => {
      if (payers.length > maxVisiblePayers) {
        const visiblePayers = payers.slice(0, maxVisiblePayers);
        return `${formatPayersList(visiblePayers)}, +${
          payers.length - maxVisiblePayers
        } more`;
      }

      return formatPayersList(payers);
    };

    return renderPayers(payers);
  },
  meta: {
    filterComponent(column, open, onClose) {
      return (
        <PayersColumnFilter
          open={open}
          onClose={onClose}
          selected={store.getState().pricing.selectedPayers}
          setSelectedAction={setSelectedPayers}
        />
      );
    },
    filterCount() {
      const state = store.getState().pricing;
      return state.selectedPayers.length;
    },
    cellStyle: {
      allowTextWrap: true,
      maxWidth: '20rem',
    },
  },
});

const spaceTypesColumnDef = columnHelper.accessor('space_types', {
  header: 'Space Types',
  cell: (props) => {
    const { space_types } = props.row.original;

    return (
      <div className="flex gap-1">
        {[...(space_types ?? [])].sort().map((space_type) => (
          <div key={space_type}>
            <Badge label={space_type} color="blue" />
          </div>
        ))}
      </div>
    );
  },
  meta: {
    filterComponent(column, open, onClose) {
      return (
        <SpaceTypeColumnFilter
          open={open}
          onClose={onClose}
          selected={store.getState().pricing.selectedSpaceTypes}
          onApply={(spaceTypes) => {
            store.dispatch(setSelectedSpaceTypes(spaceTypes));
          }}
          triggerElement={<div />}
        />
      );
    },
    filterCount() {
      const state = store.getState().pricing;
      return state.selectedSpaceTypes.length || 0;
    },
  },
});

const serviceTypesColumnDef = columnHelper.accessor('service_types', {
  header: 'Service Types',
  cell: (props) => {
    const { service_types = [] } = props.row.original;

    const types =
      service_types.length === 0 ? serviceTypes : [...service_types].sort();

    return (
      <div className="flex gap-1">
        {types.map((service_type) => (
          <Badge key={service_type} label={service_type} color="primary" />
        ))}
      </div>
    );
  },
  meta: {
    filterComponent(column, open, onClose) {
      return (
        <ServiceTypesColumnFilter
          open={open}
          onClose={onClose}
          selected={store.getState().pricing.selectedServiceTypes}
          onApply={(serviceTypes) => {
            store.dispatch(setSelectedServiceTypes(serviceTypes));
          }}
        />
      );
    },
    filterCount() {
      const state = store.getState().pricing;
      return state.selectedServiceTypes.length || 0;
    },
  },
});

const startDateColumnDef = columnHelper.display({
  enableSorting: true,
  header: 'Start Date',
  id: 'start_date',
  cell: (props) =>
    DateTime.fromISO(props.row.original.start_date).toLocaleString(),
});

const endDateColumnDef = columnHelper.display({
  enableSorting: true,
  header: 'End Date',
  id: 'end_date',
  cell: (props) =>
    DateTime.fromISO(props.row.original.end_date).toLocaleString(),
});

const baseRateColumnDef = columnHelper.accessor('base_fee_cents', {
  enableSorting: true,
  id: 'base_fee_cents',
  header: 'Base Fee',
  cell: (props) => {
    const { base_fee_cents } = props.row.original;
    return formatPriceCell(base_fee_cents);
  },
});

const loadedMileageRateColumnDef = columnHelper.accessor(
  'loaded_mileage_rate_cents',
  {
    enableSorting: true,
    id: 'loaded_mileage_rate_cents',
    header: 'Loaded Mileage Rate',
    cell: (props) => {
      const { loaded_mileage_rate_cents } = props.row.original;
      return formatPriceCell(loaded_mileage_rate_cents);
    },
  }
);

const manageColumnDef = columnHelper.display({
  header: 'Manage',
  cell: ({ row }) => <RowActions pricingSchemeId={row.original.id} />,
  meta: {
    enableHeaderMenu: false,
    headerWidth: 80,
  },
});

export const columns: ColumnDef<any, any>[] = [
  nameColumnDef,
  descriptionColumnDef,
  payersColumnDef,
  spaceTypesColumnDef,
  serviceTypesColumnDef,
  startDateColumnDef,
  endDateColumnDef,
  baseRateColumnDef,
  loadedMileageRateColumnDef,
  manageColumnDef,
];
